import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import {
  ArrowLeft,
  BarChart3,
  Download,
  LayoutGrid,
  MessageCircle,
  RefreshCw,
  Users,
} from "lucide-react";
import { Link, useParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { CrmDataProvider } from "../providers/types";
import type {
  DashboardCapabilities,
  WhatsAppBroadcastAnalytics,
  WhatsAppBroadcastRecipient,
  WhatsAppBroadcastTimelineBucket,
} from "../types";
import {
  CONTACT_WHATSAPP_SECTION_ID,
  contactWhatsAppSectionPath,
} from "../contacts/contactDeepLinks";
import { BroadcastBoardView } from "./BroadcastBoardView";
import { BroadcastGalleryFunnelCard } from "./BroadcastGalleryFunnelCard";

export function WhatsAppBroadcastAnalyticsPage() {
  const { id } = useParams();
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [analytics, setAnalytics] = useState<WhatsAppBroadcastAnalytics | null>(
    null,
  );
  const [timeline, setTimeline] = useState<WhatsAppBroadcastTimelineBucket[]>(
    [],
  );
  const [recipients, setRecipients] = useState<WhatsAppBroadcastRecipient[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [capabilities, setCapabilities] =
    useState<DashboardCapabilities | null>(null);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
  const [view, setView] = useState<"board" | "summary">("board");

  const featureFlags = capabilities?.features ?? {};
  const analyticsDisabled = featureFlags.whatsapp_broadcast_analytics === false;

  const loadCapabilities = useCallback(async () => {
    try {
      setCapabilities(await dataProvider.getDashboardCapabilities());
    } catch {
      setCapabilities(null);
    } finally {
      setCapabilitiesLoaded(true);
    }
  }, [dataProvider]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [nextAnalytics, nextTimeline, nextRecipients] = await Promise.all([
        dataProvider.getWhatsAppBroadcastAnalytics(id),
        dataProvider.getWhatsAppBroadcastTimeline(id),
        dataProvider.getWhatsAppBroadcastRecipients(id),
      ]);
      setAnalytics(nextAnalytics);
      setTimeline(nextTimeline);
      setRecipients(nextRecipients);
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, id, notify]);

  useEffect(() => {
    void loadCapabilities();
  }, [loadCapabilities]);

  useEffect(() => {
    // Some live dashboard environments expose analytics before advertising the
    // health flag. Only block the page when the API explicitly disables it.
    if (capabilitiesLoaded && !analyticsDisabled) {
      void load();
    }
  }, [analyticsDisabled, capabilitiesLoaded, load]);

  const segments = useMemo(
    () => ({
      failed: recipients.filter((item) => deliveryState(item) === "failed"),
      unread: recipients.filter((item) =>
        ["accepted", "sent", "delivered"].includes(deliveryState(item)),
      ),
      readNoAction: recipients.filter(
        (item) => deliveryState(item) === "read" && !hasCreateCommand(item),
      ),
      created: recipients.filter(hasCreateCommand),
      completed: recipients.filter(
        (item) => item.first_final_video_delivered_at,
      ),
    }),
    [recipients],
  );

  const exportCsv = async () => {
    if (!id) return;
    try {
      const csv = await dataProvider.exportWhatsAppBroadcastRecipients(id);
      downloadText(csv, `broadcast-${id}-recipients.csv`, "text/csv");
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  if (capabilitiesLoaded && analyticsDisabled) {
    return (
      <div className="space-y-4 p-6">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/whatsapp_broadcasts">
              <ArrowLeft className="size-4" />
              {translate("crm.whatsapp.broadcasts.title")}
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {translate("crm.whatsapp.analytics.title")}
          </h1>
        </div>
        <Alert>
          <BarChart3 className="size-4" />
          <AlertTitle>
            {translate("crm.whatsapp.capabilities.analytics_unavailable")}
          </AlertTitle>
          <AlertDescription>
            {translate("crm.whatsapp.analytics.not_enabled")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/whatsapp_broadcasts">
              <ArrowLeft className="size-4" />
              Broadcasts
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">Broadcast analytics</h1>
          <p className="text-sm text-muted-foreground">
            {analytics?.template_name || "WhatsApp template"}{" "}
            {analytics?.template_language || ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border p-0.5">
            <Button
              variant={view === "board" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("board")}
            >
              <LayoutGrid className="size-4" />
              Board
            </Button>
            <Button
              variant={view === "summary" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("summary")}
            >
              <BarChart3 className="size-4" />
              Summary
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className="size-4" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => void exportCsv()}>
            <Download className="size-4" />
            CSV
          </Button>
        </div>
      </div>

      {analytics?.warnings?.map((warning) => (
        <Alert key={warning.code}>
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      ))}

      {view === "board" ? (
        <BroadcastBoardView
          recipients={recipients}
          analytics={analytics}
          loading={loading}
        />
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              label="Delivered"
              value={analytics?.delivered_count}
              sublabel={formatPercent(analytics?.delivery_rate)}
            />
            <KpiCard
              label="Read"
              value={analytics?.read_count}
              sublabel={formatPercent(analytics?.read_rate)}
              helper={translate("crm.whatsapp.analytics.read_help")}
            />
            <KpiCard
              label="/create"
              value={analytics?.create_command_count}
              sublabel={formatPercent(analytics?.create_rate)}
            />
            <KpiCard label="/buy" value={analytics?.buy_count} />
            <KpiCard
              label="Echo started"
              value={analytics?.echo_started_count}
              sublabel={formatPercent(analytics?.echo_started_rate)}
            />
            <KpiCard
              label="Final delivered"
              value={analytics?.final_video_delivered_count}
              sublabel={formatPercent(analytics?.final_video_delivered_rate)}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {translate("crm.whatsapp.analytics.cost_title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <CostRow
                  label={translate("crm.whatsapp.analytics.total_spend")}
                  value={formatCurrency(
                    analytics?.total_spend,
                    analytics?.currency,
                  )}
                />
                <CostRow
                  label={translate("crm.whatsapp.analytics.cost_per_delivered")}
                  value={formatCurrency(
                    analytics?.cost_per_delivered,
                    analytics?.currency,
                  )}
                />
                <CostRow
                  label={translate("crm.whatsapp.analytics.cost_per_reply")}
                  value={formatCurrency(
                    analytics?.cost_per_reply,
                    analytics?.currency,
                  )}
                />
                <CostRow
                  label={translate("crm.whatsapp.analytics.billable_messages")}
                  value={formatNumber(analytics?.billable_message_count)}
                />
                {analytics != null &&
                  analytics.total_spend == null &&
                  analytics.cost_per_delivered == null && (
                    <p className="text-xs text-muted-foreground">
                      {translate("crm.whatsapp.analytics.cost_unavailable")}
                    </p>
                  )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {translate("crm.whatsapp.analytics.clicks_title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {analytics &&
                analytics.template_analytics_available === false ? (
                  <p className="text-xs text-muted-foreground">
                    {analytics.template_analytics_unavailable_reason ||
                      translate(
                        "crm.whatsapp.analytics.template_analytics_unavailable",
                      )}
                  </p>
                ) : (
                  <>
                    <CostRow
                      label={translate(
                        "crm.whatsapp.analytics.clicked_url_button",
                      )}
                      value={formatNumber(
                        analytics?.template_clicked_url_button,
                      )}
                    />
                    <CostRow
                      label={translate(
                        "crm.whatsapp.analytics.clicked_quick_reply",
                      )}
                      value={formatNumber(
                        analytics?.template_clicked_quick_reply,
                      )}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {id && <BroadcastGalleryFunnelCard broadcastId={id} />}

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <Card>
              <CardHeader>
                <CardTitle>Funnel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <FunnelRow
                  label="Eligible"
                  value={analytics?.eligible_count}
                  total={analytics?.eligible_count}
                />
                <FunnelRow
                  label="Sent"
                  value={analytics?.sent_count}
                  total={analytics?.eligible_count}
                />
                <FunnelRow
                  label="Delivered"
                  value={analytics?.delivered_count}
                  total={analytics?.eligible_count}
                />
                <FunnelRow
                  label="Read"
                  value={analytics?.read_count}
                  total={analytics?.delivered_count}
                />
                <FunnelRow
                  label="Inbound"
                  value={analytics?.inbound_count}
                  total={analytics?.delivered_count}
                />
                <FunnelRow
                  label="/create"
                  value={analytics?.create_command_count}
                  total={analytics?.delivered_count}
                />
                <FunnelRow
                  label="/buy"
                  value={analytics?.buy_count}
                  total={analytics?.delivered_count}
                />
                <FunnelRow
                  label="Echo started"
                  value={analytics?.echo_started_count}
                  total={analytics?.delivered_count}
                />
                <FunnelRow
                  label="Final delivered"
                  value={analytics?.final_video_delivered_count}
                  total={analytics?.delivered_count}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Segments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Segment label="Failed" value={segments.failed.length} />
                <Segment
                  label="Delivered unread"
                  value={segments.unread.length}
                />
                <Segment
                  label="Read no action"
                  value={segments.readNoAction.length}
                />
                <Segment label="/create" value={segments.created.length} />
                <Segment
                  label="Completed Echo"
                  value={segments.completed.length}
                />
                <Segment
                  label={`No conversion after ${analytics?.attribution_window_hours ?? 72}h`}
                  value={analytics?.ignored_no_conversion_count ?? 0}
                />
                <p className="text-xs text-muted-foreground">
                  {translate("crm.whatsapp.analytics.inferred_help")}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {timeline.length ? (
                <div className="space-y-2">
                  {timeline.slice(0, 24).map((bucket) => (
                    <div
                      key={bucket.bucket}
                      className="grid grid-cols-[10rem_minmax(0,1fr)] items-center gap-3 text-sm"
                    >
                      <span className="text-muted-foreground">
                        {formatDate(bucket.bucket)}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <TimelineBadge label="sent" value={bucket.sent} />
                        <TimelineBadge
                          label="delivered"
                          value={bucket.delivered}
                        />
                        <TimelineBadge label="read" value={bucket.read} />
                        <TimelineBadge
                          label="/create"
                          value={bucket.create_command}
                        />
                        <TimelineBadge
                          label="final"
                          value={bucket.final_video_delivered}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Timeline data will appear as status and conversion events
                  arrive.
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recipients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[32rem] overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Read</TableHead>
                      <TableHead>
                        {translate("crm.whatsapp.analytics.reply")}
                      </TableHead>
                      <TableHead>/create</TableHead>
                      <TableHead>Echo</TableHead>
                      <TableHead>Final</TableHead>
                      <TableHead>
                        {translate("crm.whatsapp.analytics.pricing_category")}
                      </TableHead>
                      <TableHead>Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.map((recipient) => (
                      <TableRow key={String(recipient.id)}>
                        <TableCell>
                          <div className="font-medium">
                            {recipient.contact_name || recipient.contact_id}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {recipient.whatsapp_user_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={statusVariant(deliveryState(recipient))}
                          >
                            {deliveryState(recipient)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <ReadCell recipient={recipient} />
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <ReplyCell recipient={recipient} />
                        </TableCell>
                        <TableCell>
                          {formatDate(recipient.first_create_command_at)}
                        </TableCell>
                        <TableCell>
                          {formatDate(recipient.first_echo_started_at)}
                        </TableCell>
                        <TableCell>
                          {formatDate(recipient.first_final_video_delivered_at)}
                        </TableCell>
                        <TableCell>
                          {recipient.pricing_category || "-"}
                        </TableCell>
                        <TableCell>
                          {recipient.attribution_confidence || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!recipients.length && (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center">
                          {loading ? "Loading" : "No recipients"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sublabel,
  helper,
}: {
  label: string;
  value?: number;
  sublabel?: string;
  helper?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value ?? 0}</div>
        <div className="text-xs text-muted-foreground">{sublabel || "-"}</div>
        {helper && (
          <div className="mt-1 text-xs text-muted-foreground">{helper}</div>
        )}
      </CardContent>
    </Card>
  );
}

function CostRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function ReplyCell({ recipient }: { recipient: WhatsAppBroadcastRecipient }) {
  const translate = useTranslate();
  const conversationHref = getConversationHref(recipient);

  if (!recipient.reply_text && !conversationHref) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="space-y-1">
      {recipient.reply_text && (
        <div className="line-clamp-2 text-sm" title={recipient.reply_text}>
          {recipient.reply_text}
        </div>
      )}
      {conversationHref && (
        <Link
          to={conversationHref}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <MessageCircle className="size-3" />
          {translate("crm.whatsapp.analytics.open_conversation")}
        </Link>
      )}
    </div>
  );
}

function ReadCell({ recipient }: { recipient: WhatsAppBroadcastRecipient }) {
  const readAt = effectiveReadAt(recipient);
  if (!readAt) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="space-y-1">
      <div>{formatDate(readAt)}</div>
      {!recipient.first_read_at && (
        <Badge variant="outline" className="text-[10px]">
          inferred
        </Badge>
      )}
    </div>
  );
}

function getConversationHref(recipient: WhatsAppBroadcastRecipient) {
  if (!recipient.reply_text && !recipient.conversation_deep_link) return null;

  const contactId = String(recipient.contact_id ?? "").trim();
  if (contactId) {
    return contactWhatsAppSectionPath(contactId);
  }

  const deepLink = recipient.conversation_deep_link?.trim();
  if (!deepLink || !deepLink.startsWith("/")) return null;

  return `${deepLink.split("#")[0]}#${CONTACT_WHATSAPP_SECTION_ID}`;
}

function FunnelRow({
  label,
  value,
  total,
}: {
  label: string;
  value?: number;
  total?: number;
}) {
  const pct = percentage(value, total);
  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)_5rem] items-center gap-3">
      <span className="text-sm">{label}</span>
      <Progress value={pct} />
      <span className="text-right text-sm tabular-nums">
        {value ?? 0} {pct ? `(${Math.round(pct)}%)` : ""}
      </span>
    </div>
  );
}

function Segment({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function TimelineBadge({ label, value }: { label: string; value?: number }) {
  if (!value) return null;
  return (
    <Badge variant="secondary" className="gap-1">
      <Users className="size-3" />
      {value} {label}
    </Badge>
  );
}

function deliveryState(recipient: WhatsAppBroadcastRecipient) {
  if (effectiveReadAt(recipient) || recipient.provider_status === "read")
    return "read";
  if (
    recipient.first_delivered_at ||
    recipient.provider_status === "delivered"
  ) {
    return "delivered";
  }
  if (recipient.first_sent_at || recipient.provider_status === "sent")
    return "sent";
  return recipient.provider_status || recipient.status || "accepted";
}

function effectiveReadAt(recipient: WhatsAppBroadcastRecipient) {
  return (
    recipient.first_read_at ||
    recipient.first_inbound_after_broadcast_at ||
    recipient.first_create_command_at ||
    recipient.first_echo_started_at ||
    recipient.first_generation_requested_at ||
    recipient.first_final_video_created_at ||
    recipient.first_final_video_delivered_at ||
    (hasCreateCommand(recipient)
      ? recipient.first_inbound_after_broadcast_at ||
        recipient.status_updated_at ||
        recipient.first_delivered_at ||
        recipient.sent_at ||
        recipient.accepted_at
      : null) ||
    (recipient.provider_status === "read" ? recipient.status_updated_at : null)
  );
}

function hasCreateCommand(recipient: WhatsAppBroadcastRecipient) {
  if (recipient.first_create_command_at) return true;
  const text = recipient.reply_text?.trim().toLowerCase().replace(/\s+/g, " ");
  if (!text) return false;
  return (
    text.split(" ")[0] === "/create" ||
    [
      "create",
      "create movie",
      "create my movie",
      "create new echo",
      "start my movie",
      "start new echo",
    ].includes(text)
  );
}

function percentage(value?: number, total?: number) {
  if (!value || !total) return 0;
  return Math.max(0, Math.min(100, (value / total) * 100));
}

function formatPercent(value?: number) {
  if (value == null) return "-";
  return `${Math.round(value * 100)}%`;
}

function formatNumber(value?: number | null) {
  if (value == null) return "—";
  return new Intl.NumberFormat().format(value);
}

function formatCurrency(value?: number | null, currency?: string | null) {
  if (value == null) return "—";
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(value);
    } catch {
      // Fall through to a plain number when the currency code is invalid.
    }
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function statusVariant(
  status: string,
): "destructive" | "secondary" | "outline" {
  if (status === "failed") return "destructive";
  if (["read", "delivered"].includes(status)) return "secondary";
  return "outline";
}

function downloadText(text: string, filename: string, type: string) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "crm.whatsapp.error";
}
